import { HTMLAttributes } from 'react'

import Container from '@/components/base/Container'
import Cta from '@/components/base/Cta'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Typography, { TypographyProps } from '@/components/base/Typography'

import { doubleParagraph } from './animations'
import { cn } from '@/lib/utils'
import { WPLink, WPPageData } from '@/types'

export type DoubleParagraphProps = HTMLAttributes<HTMLElement> &
  Partial<{
    paragraph_dx: {
      pretitle: string
      title: string
      title_component: TypographyProps['component']
      paragraph: string
      button: WPLink
    }
    paragraph_sx: {
      pretitle: string
      title: string
      title_component: TypographyProps['component']
      paragraph: string
      button: WPLink
    }
    theme: WPPageData['acf']['theme']
    alignment: 'left' | 'right'
  }>

const DoubleParagraph = ({
  paragraph_dx,
  paragraph_sx,
  theme,
  alignment = 'left',
  ...props
}: DoubleParagraphProps) => {
  return (
    <Motion animation={doubleParagraph}>
      {(motionRef) => (
        <section
          ref={motionRef}
          {...props}
          className={cn([
            {
              'bg-dark-100 text-dark-500': theme === 'light',
              'bg-dark-600 text-white': theme === 'dark'
            }
          ])}
        >
          <Container>
            <Grid className="gap-y-[6.4rem]">
              {paragraph_sx ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col gap-[1.6rem] lg:gap-[3.2rem]',
                    'md:col-span-6',
                    'lg:col-span-5',
                    {
                      'lg:col-start-2': alignment === 'left',
                      'lg:col-start-7': alignment === 'right'
                    }
                  ])}
                >
                  {paragraph_sx.pretitle ? (
                    <Typography
                      variant="label"
                      className="font-extrabold"
                      data-motion-pretitle
                    >
                      {paragraph_sx.pretitle}
                    </Typography>
                  ) : null}
                  {paragraph_sx.title ? (
                    <Typography
                      variant="h3"
                      className="!font-normal [&_strong]:font-bold"
                      component={paragraph_sx.title_component}
                      data-motion-title
                    >
                      {paragraph_sx.title}
                    </Typography>
                  ) : null}
                  {paragraph_sx.paragraph ? (
                    <Typography variant="body" data-motion-paragraph>
                      {paragraph_sx.paragraph}
                    </Typography>
                  ) : null}
                  {paragraph_sx.button ? (
                    <Cta
                      link={paragraph_sx.button}
                      theme="light"
                      data-motion-cta
                    />
                  ) : null}
                </div>
              ) : null}
              {paragraph_dx ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col gap-[1.6rem] lg:gap-[3.2rem]',
                    'md:col-span-6',
                    'lg:col-span-4',
                    {
                      'lg:col-start-8': alignment === 'left',
                      '-order-1 lg:col-start-2': alignment === 'right'
                    }
                  ])}
                >
                  {paragraph_dx.pretitle ? (
                    <Typography variant="label" data-motion-pretitle>
                      {paragraph_dx.pretitle}
                    </Typography>
                  ) : null}
                  {paragraph_dx.title ? (
                    <Typography
                      variant="h2"
                      className="!font-normal [&_strong]:font-bold"
                      component={paragraph_dx.title_component}
                      data-motion-title
                    >
                      {paragraph_dx.title}
                    </Typography>
                  ) : null}
                  {paragraph_dx.paragraph ? (
                    <Typography variant="body" data-motion-paragraph>
                      {paragraph_dx.paragraph}
                    </Typography>
                  ) : null}
                  {paragraph_dx.button ? (
                    <Cta
                      link={paragraph_dx.button}
                      theme="light"
                      data-motion-cta
                    />
                  ) : null}
                </div>
              ) : null}
            </Grid>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default DoubleParagraph
